import { useEffect, useMemo, useRef, useState } from 'react'

import { Option, OptionGroup } from '@joor/design-system'
import { CSSObject } from 'styled-components'

import { OrphanRetailer } from '__generated__/atlas-types'

import { BrandStartOrderIds } from 'modals/OrderList/StartOrderModal/StartOrderModal.ids'

import {
  NEW_CUSTOMER_KEY,
  ORPHAN_CUSTOMER_KEY,
  flattenOptionGroups,
  getDesignSystemOptionId,
} from '../../BrandStartOrderModal.formatters'
import messages from '../../BrandStartOrderModal.messages'
import { DropdownContainer, StyledDropdown } from './RetailerDropdown.styles'

const CUSTOMER_NAME_MAX_LENGTH = 255

export interface RetailerSelectionState {
  selectedDoorIds?: string[]
  selectedRetailerId?: string | null
  selectedOrphanRetailerId?: string | null
  isAddingNewCustomer?: boolean
}

interface RetailerDropdownProps {
  loading: boolean
  loadingDataForRetailer: boolean
  options: OptionGroup[]
  value: string | null | undefined
  retailerSearchText: string
  selectedRetailerId: string | null | undefined
  validCustomerNameSelected: boolean
  hasPriceTypeMatches: boolean
  orphanRetailers: OrphanRetailer[]
  isAddCostumerRestricted: boolean
  setRetailerSelection: (selection: RetailerSelectionState) => void
  setRetailerId: (retailerId?: string | null) => void
  setOrphanRetailer: (orphanRetailer: Partial<OrphanRetailer> | null) => void
  changeRetailerSearchText: (searchText: string) => void
  changeCollectionSearchText: (searchText: string) => void
}

export const RetailerDropdown = ({
  loading,
  loadingDataForRetailer,
  options,
  value,
  retailerSearchText,
  selectedRetailerId,
  validCustomerNameSelected,
  hasPriceTypeMatches,
  orphanRetailers,
  isAddCostumerRestricted,
  setRetailerSelection,
  setRetailerId,
  setOrphanRetailer,
  changeRetailerSearchText,
  changeCollectionSearchText,
}: RetailerDropdownProps) => {
  const [searchText, setSearchText] = useState('')
  const [isNewSearch, setIsNewSearch] = useState(false)
  const [selectedInternalOption, setSelectedInternalOption] =
    useState<Option | null>(null)
  const retailerSearchTimeout = useRef<NodeJS.Timeout | null>(null)
  const newCustomerOptionId = !isAddCostumerRestricted
    ? options[options.length - 1]?.options[0]?.id
    : undefined

  const dropdownNoOptionsMessage = useMemo(() => {
    if (!loading && !flattenOptionGroups(options).length && !!searchText) {
      return messages.noRetailersFound
    }
    return undefined
  }, [loading, searchText, options])

  useEffect(() => {
    setSearchText(retailerSearchText)
    return () => {
      if (retailerSearchTimeout.current) {
        clearTimeout(retailerSearchTimeout.current)
      }
    }
  }, [])

  const selectedOption = useMemo(() => {
    if (loading || !value || isNewSearch) {
      return null
    }
    if (selectedInternalOption) {
      return selectedInternalOption
    }
    const selected = flattenOptionGroups(options).find(
      (option) => getDesignSystemOptionId(option) === value,
    )
    return selected ?? null
  }, [options, value, loading, selectedInternalOption, isNewSearch])

  const handleSelect = ({ selected }: { selected: Option }) => {
    const newValue = getDesignSystemOptionId(selected)
    setSelectedInternalOption(selected)
    setSearchText('')
    setIsNewSearch(false)

    if (newValue.startsWith(NEW_CUSTOMER_KEY)) {
      setRetailerSelection({
        isAddingNewCustomer: true,
        selectedRetailerId: null,
        selectedOrphanRetailerId: null,
      })
      setOrphanRetailer({ retailerName: searchText })
      changeCollectionSearchText('')
      return
    }

    if (newValue.startsWith(ORPHAN_CUSTOMER_KEY)) {
      const orphanRetailerId = newValue.split(ORPHAN_CUSTOMER_KEY)[1]
      const selectedOrphanRetailer = orphanRetailers.find(
        (orphanRetailer) => orphanRetailer.id === orphanRetailerId,
      )
      setRetailerSelection({
        isAddingNewCustomer: false,
        selectedRetailerId: null,
        selectedOrphanRetailerId: newValue,
      })
      setOrphanRetailer(selectedOrphanRetailer ?? null)
      changeCollectionSearchText('')
      return
    }

    if (newValue !== selectedRetailerId) {
      setRetailerSelection({
        selectedDoorIds: [],
        selectedRetailerId: newValue,
        selectedOrphanRetailerId: null,
        isAddingNewCustomer: false,
      })
      setRetailerId(newValue)
      setOrphanRetailer(null)
      changeCollectionSearchText('')
    }
  }

  const onRetailerInputChange = (inputText: string) => {
    let truncatedInputText = inputText
    if (inputText.length > CUSTOMER_NAME_MAX_LENGTH) {
      truncatedInputText = inputText.slice(0, CUSTOMER_NAME_MAX_LENGTH)
    }
    setSearchText(truncatedInputText)
    if (retailerSearchTimeout.current) {
      clearTimeout(retailerSearchTimeout.current)
    }
    retailerSearchTimeout.current = setTimeout(() => {
      if (truncatedInputText && truncatedInputText !== retailerSearchText) {
        changeRetailerSearchText(truncatedInputText)
      }
    }, 500)
  }

  const onFocus = () => {
    setIsNewSearch(true)
    setSearchText('')
  }

  const onBlur = () => {
    setIsNewSearch(false)
  }

  const getErrorMessage = () => {
    if (
      validCustomerNameSelected &&
      !hasPriceTypeMatches &&
      !loadingDataForRetailer
    ) {
      return selectedRetailerId
        ? messages.noPriceTypeAvailableForThisRetailer
        : messages.noPriceTypeAvailable
    }

    return undefined
  }

  return (
    <DropdownContainer>
      <StyledDropdown
        key={selectedInternalOption ? selectedInternalOption.value : 'empty'}
        id={BrandStartOrderIds.RetailerOptions}
        testId={BrandStartOrderIds.RetailerOptions}
        labelId={BrandStartOrderIds.RetailerTitle}
        label={messages.selectRetailer}
        isRequired
        options={options}
        value={isNewSearch ? undefined : selectedOption}
        translateOptions={false}
        onSelect={handleSelect}
        onInputChange={onRetailerInputChange}
        inputValue={selectedOption?.label || searchText}
        isSearchable
        isLoading={loading}
        placeholder={messages.startTyping}
        onFocus={onFocus}
        onBlur={onBlur}
        menuIsOpen={isNewSearch && searchText.length > 1}
        noOptionsMessage={() => dropdownNoOptionsMessage}
        maxLength={CUSTOMER_NAME_MAX_LENGTH}
        errorMessage={getErrorMessage()}
        dropdownIndicatorIconName="search"
        isDropdownIndicatorIconAnimated={false}
        newCustomerOptionId={newCustomerOptionId}
        styles={{
          groupHeading: (b: CSSObject) => ({
            ...b,
            marginLeft: '-8px',
          }),
          option: (b: CSSObject) => ({ ...b, padding: '8px 24px' }),
        }}
      />
    </DropdownContainer>
  )
}
